import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { Braces, Cloud, Maximize2, Minimize2, Monitor, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  STAFF_PROXY_CAPTURE_DEVICE_SCREENSHOT,
  STAFF_PROXY_TESTING_COMMAND_EXECUTIONS,
  STAFF_PROXY_TESTING_DEVICE_ACTIVITY_FEED,
  STAFF_TESTING_DEVICE_STAFF_ACTIONS,
} from '@staff-app/graphql-client';
import {
  Button,
  InShellPanel,
  InShellPanelDescription,
  InShellPanelHeader,
  InShellPanelTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@staff-app/ui';
import {
  formatModel,
  formatOs,
  type AgentDevice,
  type DeviceClassification,
} from '@staff-app/shared-types';
import type { MdmDevice } from '@/features/mdm-devices/hooks/useStaffCustomerDevices';
import {
  useDeviceHeartbeats,
  type LiveDeviceStatus,
} from '@/features/mdm-devices/hooks/useDeviceHeartbeats';
import { classifyDeviceStatus } from '@/features/mdm-devices/components/DeviceStatusDot';
import { DeviceHealthStrip } from '@/features/mdm-devices/components/DeviceHealthStrip';
import { DeviceInfoList } from '@/features/mdm-devices/components/DeviceInfoList';
import { DeviceJsonDialog } from '@/features/mdm-devices/components/DeviceJsonDialog';
import { DeviceCapabilityDiagnostic } from '@/features/customers/components/DeviceCapabilityDiagnostic';
import { DeviceTroubleshootTab } from '@/features/troubleshoot/components/DeviceTroubleshootTab';
import { TESTING_DEVICE_REQUIRED_MESSAGE } from '../../lib/testing-account';
import { DrawerAccountContext, type DeviceDrawerMode } from '../../lib/drawer-mode';
import { useTestingDeviceDetail } from '../../hooks/useTestingDeviceDetail';
import { useTestingMdmLiveInfo } from '../../hooks/useTestingMdmLiveInfo';
import { useTestingDeviceCommands } from '../../hooks/useTestingDeviceCommands';
import { ActivityTab } from './ActivityTab';
import { TestingActionBar } from './TestingActionBar';
import { TestingCommandHistoryDialog } from './dialogs/TestingCommandHistoryDialog';
import { TestingDeviceInfoDialog } from './dialogs/TestingDeviceInfoDialog';
import { TestingRemoteControlDialog } from './dialogs/TestingRemoteControlDialog';
import { TestingStorageDialog } from './dialogs/TestingStorageDialog';
import type { TestingCommandDialogProps } from './dialogs/TestingDialogFrame';
import { TestingExecuteCommandDialog } from './dialogs/TestingExecuteCommandDialog';
import { TestingHdmiCecDialog } from './dialogs/TestingHdmiCecDialog';
import { TestingInstallApkDialog } from './dialogs/TestingInstallApkDialog';
import { TestingInstallVersionDialog } from './dialogs/TestingInstallVersionDialog';
import { TestingOtaDialog } from './dialogs/TestingOtaDialog';
import { TestingRs232Dialog } from './dialogs/TestingRs232Dialog';
import { TestingTimezoneDialog } from './dialogs/TestingTimezoneDialog';
import { TestingWifiDialog } from './dialogs/TestingWifiDialog';
import { SettingsTab } from './SettingsTab';
import { supportsSettings } from './lib/settings-payload';
import { screenshotBlockedReason } from './lib/testing-actions';
import { ConnectivitySection } from './ConnectivitySection';
import { ContentTab } from './ContentTab';
import { DeviceStateCards } from './DeviceStateCards';
import { OnTheGlassFacts } from './OnTheGlassFacts';
import { getCapabilityDisplay } from './lib/capability-display';
import { classifyConnectivity } from './lib/connectivity';
import { getScreenHealthVerdict } from './lib/screen-health';
import type { TestingDeviceDetail } from './lib/types';

/** The one open device is polled faster than the page list (30s). */
const DRAWER_HEARTBEAT_POLL_MS = 10_000;

/** Queries an action can change — refetched (when mounted) after any action settles. */
const ACTION_REFETCH_QUERIES = new Set(
  [
    STAFF_PROXY_TESTING_DEVICE_ACTIVITY_FEED,
    STAFF_TESTING_DEVICE_STAFF_ACTIONS,
    STAFF_PROXY_TESTING_COMMAND_EXECUTIONS,
  ].map((doc) => {
    const def = doc.definitions[0];
    return def && 'name' in def ? def.name?.value : undefined;
  }),
);

interface ChildDialog {
  id: string;
  context?: Record<string, unknown>;
}

interface CaptureResult {
  staffProxyCaptureDeviceScreenshot: {
    status: string;
    reason?: string | null;
    imageKey?: string | null;
    capturedAt?: string | null;
  } | null;
}

type PillStatus = LiveDeviceStatus | 'stale';

/** Header status pill — scio3's `DeviceStatusBadge` vocabulary on a white chip in the banner. */
const PILL: Record<PillStatus, { label: string; dot: string; text: string }> = {
  online: {
    label: 'Online',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700 dark:text-emerald-400',
  },
  hdmiOff: {
    label: 'HDMI off',
    dot: 'border border-amber-500 bg-gradient-to-r from-amber-500 from-50% to-transparent to-50%',
    text: 'text-amber-700 dark:text-amber-500',
  },
  stale: {
    label: 'Stale',
    dot: 'border border-amber-500 bg-gradient-to-r from-amber-500 from-50% to-transparent to-50%',
    text: 'text-amber-700 dark:text-amber-500',
  },
  offline: {
    label: 'Offline',
    dot: 'border border-muted-foreground bg-transparent',
    text: 'text-muted-foreground',
  },
};

// Underline-style drawer tabs (scio3 `DRAWER_TAB_TRIGGER`) — overrides the pill defaults of the shared
// Tabs via twMerge for THIS drawer only: transparent bg, no radius/shadow, a 2px bottom border that
// turns primary when active, pulled down 1px to sit on the list's bottom rule.
const DRAWER_TAB_TRIGGER =
  'cursor-pointer -mb-px rounded-none border-b-2 border-transparent bg-transparent px-3 py-2 text-sm font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none';

/** Scrollable tab body — bleeds into the panel's right gutter so the scrollbar hugs the edge. */
const TAB_BODY = '-mr-6 mt-0 min-h-0 flex-1 overflow-y-auto pb-4 pr-6 scroll-thin';

interface Props {
  /** The selected My Devices row; null closes the drawer. */
  agentDevice: AgentDevice | null;
  onClose: () => void;
  /** Page-level live status map (30s poll) — the fallback before the drawer's own poll lands. */
  liveStatusByUuid?: Map<string, LiveDeviceStatus>;
}

/**
 * The device drawer — scio3's MDM device drawer, in two modes (`DeviceDrawerMode`):
 *
 * `TestingDeviceDrawer` (My Testing Devices), decided per row by `isTestingDevice`:
 *  - testing device → live header (model · OS · capability) + the action bar + Overview / Settings
 *    (managed hardware only) / Content / Activity / Troubleshooting, read and written through the
 *    testing-device proxy ops;
 *  - any other row → the pairing notice + the read-only info the list already has. NONE of the
 *    testing ops are issued: staff-api 403s them for a non-testing device.
 *
 * `CustomerDeviceDrawer` (the customer Devices page) — the SAME drawer: header, action bar, Overview /
 * Settings / Content / Activity / Troubleshooting, Capture now and "+ Start session", plus a persistent
 * "Customer device" line under the banner. Every op that takes an `accountId` carries the page's
 * (staff-api's customer gate: the device must be on that account); the generic proxy ops take none.
 * `availableCommands` is open-api's resolution against the customer's REAL plan, so the action list
 * follows the plan with no staff override.
 *
 * The action bar sits directly under the header (between it and the tabs) —
 * `TESTING_ACTION_ALLOWLIST` ∩ open-api `availableCommands`; its dialog-driven commands (time zone,
 * Wi-Fi, installs, OTA, HDMI CEC, RS232, execute) open the phase-3 dialogs via `openChildDialog`.
 */
export function TestingDeviceDrawer({ agentDevice, onClose, liveStatusByUuid }: Props) {
  const device = agentDevice?.device ?? null;
  return (
    <DrawerShell
      openFor={agentDevice?.deviceId ?? null}
      onClose={onClose}
      testId="testing-device-drawer"
    >
      {(chrome) =>
        agentDevice && device ? (
          agentDevice.isTestingDevice ? (
            <DeviceDrawerBody
              key={agentDevice.deviceId}
              mode="testing"
              deviceId={agentDevice.deviceId}
              device={device}
              assign={agentDevice}
              pageLiveStatus={device.UUID ? liveStatusByUuid?.get(device.UUID) : undefined}
              chrome={chrome}
            />
          ) : (
            <NonTestingDeviceBody
              key={agentDevice.deviceId}
              device={device}
              liveStatus={device.UUID ? liveStatusByUuid?.get(device.UUID) : undefined}
              chrome={chrome}
            />
          )
        ) : null
      }
    </DrawerShell>
  );
}

interface CustomerProps {
  /** The customer whose Devices page this is — every accountId-taking drawer op is gated to it. */
  accountId: string;
  /** The customer's company name, when the page already has it — shown in the customer-device line. */
  companyName?: string | null;
  /** The page's loaded device list; the open device is looked up here by `_id` or UUID. */
  devices: MdmDevice[];
  /** The open device (`_id` or UUID, e.g. from `?device=`); null closes the drawer. */
  deviceId: string | null;
  onClose: () => void;
  /** Click the "Current assign id" row → push that content to my testing devices. */
  onAssignClick?: (contentId: string) => void;
  /** Page-level live status map (open-api heartbeat) — the fallback before the drawer's poll lands. */
  liveStatusByUuid?: Map<string, LiveDeviceStatus>;
}

/** The customer Devices page drawer — customer mode (see `TestingDeviceDrawer`). */
export function CustomerDeviceDrawer({
  accountId,
  companyName,
  devices,
  deviceId,
  onClose,
  onAssignClick,
  liveStatusByUuid,
}: CustomerProps) {
  const device = deviceId
    ? devices.find((d) => d._id === deviceId || d.UUID === deviceId)
    : undefined;
  return (
    <DrawerShell openFor={deviceId} onClose={onClose} testId="customer-device-drawer">
      {(chrome) =>
        device ? (
          <DeviceDrawerBody
            key={device._id}
            mode="customer"
            accountId={accountId}
            companyName={companyName}
            deviceId={device._id}
            device={device}
            assign={device}
            pageLiveStatus={device.UUID ? liveStatusByUuid?.get(device.UUID) : undefined}
            chrome={chrome}
            onAssignClick={onAssignClick}
          />
        ) : deviceId ? (
          // The id matched no loaded row (another team / stale deep-link, or the list is loading).
          <div className="flex h-full flex-col">
            <InShellPanelHeader className="shrink-0 px-6 pt-5">
              <InShellPanelTitle>{'Device unavailable'}</InShellPanelTitle>
              <InShellPanelDescription className="sr-only">
                {'Device detail panel'}
              </InShellPanelDescription>
            </InShellPanelHeader>
            <div
              className="px-6 pt-4 text-sm text-muted-foreground"
              data-testid="customer-device-drawer-not-found"
            >
              {"This device isn't in the current list. Refresh or switch teams to view it."}
            </div>
          </div>
        ) : null
      }
    </DrawerShell>
  );
}

/**
 * The shared `InShellPanel` shell: scio3's in-place width toggle (720px ⇄ min(1100px, 96vw)), keyed to
 * the open device so closing or switching devices reopens at the default width.
 */
function DrawerShell({
  openFor,
  onClose,
  testId,
  children,
}: {
  openFor: string | null;
  onClose: () => void;
  testId: string;
  children: (chrome: BannerChrome) => ReactNode;
}) {
  const [expandedFor, setExpandedFor] = useState<string | null>(null);
  const expanded = !!openFor && expandedFor === openFor;
  const chrome: BannerChrome = {
    expanded,
    onToggleExpand: () => setExpandedFor(expanded ? null : openFor),
    onClose,
  };

  return (
    <InShellPanel
      open={!!openFor}
      onOpenChange={(next) => !next && onClose()}
      side="right"
      data-testid={testId}
      closeLabel={'Close'}
      // The banner carries the one ✕ (and ⤢), white on teal — so the panel's own close is hidden.
      hideCloseButton
      className={cn(
        'flex w-full flex-col gap-0 overflow-hidden p-0',
        expanded
          ? 'sm:w-[min(1100px,96vw)] sm:max-w-[min(1100px,96vw)]'
          : 'sm:w-[min(720px,96vw)] sm:max-w-[min(720px,96vw)]',
      )}
    >
      {children(chrome)}
    </InShellPanel>
  );
}

interface BannerChrome {
  expanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Non-testing row: notice + list-row info only.
// ---------------------------------------------------------------------------

function NonTestingDeviceBody({
  device,
  liveStatus,
  chrome,
}: {
  device: MdmDevice;
  liveStatus?: LiveDeviceStatus;
  chrome: BannerChrome;
}) {
  const status: PillStatus = liveStatus ?? classifyDeviceStatus(device);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <TestingBanner
        name={device.deviceName ?? device._id}
        status={status}
        detail={null}
        device={device}
        chrome={chrome}
      />

      <div className="flex min-h-0 flex-1 flex-col px-6 pt-4">
        <div
          className="mb-4 shrink-0 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning"
          data-testid="agent-device-not-testing-notice"
        >
          {TESTING_DEVICE_REQUIRED_MESSAGE}
        </div>

        <div className="-mr-6 min-h-0 flex-1 space-y-4 overflow-y-auto pb-4 pr-6 scroll-thin">
          <DeviceHealthStrip device={device} liveStatus={liveStatus} />
          <div
            className="rounded-lg border bg-card p-3 shadow-sm"
            data-testid="testing-device-info"
          >
            <div className="mb-2 text-xs font-semibold text-muted-foreground">{'Device info'}</div>
            <DeviceInfoList device={device} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Testing device: live header + tabs.
// ---------------------------------------------------------------------------

type TabId = 'overview' | 'settings' | 'content' | 'activity' | 'troubleshoot';

/** Catalog `open-dialog` ids → the phase-3 command dialogs (one device, testing proxies only). */
const COMMAND_DIALOGS: Record<string, (props: TestingCommandDialogProps) => ReactNode> = {
  timezone: TestingTimezoneDialog,
  wifiManagement: TestingWifiDialog,
  installVersion: TestingInstallVersionDialog,
  installApk: TestingInstallApkDialog,
  otaUpdate: TestingOtaDialog,
  hdmiCec: TestingHdmiCecDialog,
  rs232Send: TestingRs232Dialog,
  execute: TestingExecuteCommandDialog,
};

/** What's on the screen — the My Devices row carries it resolved; a customer row has it itself. */
type AssignFields = Pick<AgentDevice, 'currentType' | 'currentAssignId' | 'currentAssignName'>;

/** Overlay open-api's detail onto the list row so the reused list components see live values. */
function mergeDevice(
  assign: AssignFields,
  device: MdmDevice,
  detail: TestingDeviceDetail | null,
): MdmDevice {
  const base: MdmDevice = {
    ...device,
    currentType: assign.currentType ?? device.currentType,
    currentAssignId: assign.currentAssignId ?? device.currentAssignId,
    currentAssignName: assign.currentAssignName ?? device.currentAssignName,
  };
  if (!detail) return base;
  const pick = <T,>(v: T | null | undefined, fallback: T | undefined) => v ?? fallback;
  return {
    ...base,
    deviceName: pick(detail.deviceName, base.deviceName),
    os: pick(detail.os, base.os),
    osVersion: pick(detail.osVersion, base.osVersion),
    model: pick(detail.model, base.model),
    platform: pick(detail.platform, base.platform),
    manufacturer: pick(detail.manufacturer, base.manufacturer),
    serialNo: pick(detail.serialNo, base.serialNo),
    localAppVersion: pick(detail.localAppVersion, base.localAppVersion),
    lastHeartBeat: pick(detail.liveLastHeartBeat ?? detail.lastHeartBeat, base.lastHeartBeat),
    activationDate: pick(detail.activationDate, base.activationDate),
    tz: pick(detail.tz, base.tz),
    usedStorage: pick(detail.usedStorage, base.usedStorage),
    totalStorage: pick(detail.totalStorage, base.totalStorage),
    fleetId: detail.fleetId ?? base.fleetId,
    // open-api's own resolution (matrix ∩ plan ∩ state ∩ reported ∩ fleet) — staff-api's are stubs.
    classification: (detail.classification as DeviceClassification | null) ?? base.classification,
    availableCommands: detail.availableCommands ?? base.availableCommands,
    reportedCapabilities: detail.reportedCapabilities ?? base.reportedCapabilities,
  };
}

function DeviceDrawerBody({
  mode,
  accountId: customerAccountId,
  companyName,
  deviceId,
  device,
  assign,
  pageLiveStatus,
  chrome,
  onAssignClick,
}: {
  mode: DeviceDrawerMode;
  /** Customer mode: the page's account — sent with every accountId-taking op (customer gate). */
  accountId?: string;
  /** Customer mode: the company name for the customer-device line (falls back to the account id). */
  companyName?: string | null;
  deviceId: string;
  device: MdmDevice;
  assign: AssignFields;
  pageLiveStatus?: LiveDeviceStatus;
  chrome: BannerChrome;
  /** Customer mode: "Current assign id" → push that content to my testing devices. */
  onAssignClick?: (contentId: string) => void;
}) {
  const isTesting = mode === 'testing';
  // Only customer mode scopes the ops; testing mode never sends an accountId (testing gate).
  const scopeAccountId = isTesting ? undefined : customerAccountId;
  const [tab, setTab] = useState<TabId>('overview');
  const [jsonOpen, setJsonOpen] = useState(false);
  const [childDialog, setChildDialog] = useState<ChildDialog | null>(null);
  const [captureNote, setCaptureNote] = useState<string | null>(null);

  const {
    detail,
    loading: detailLoading,
    error: detailError,
    refetch: refetchDetail,
  } = useTestingDeviceDetail(deviceId, false, scopeAccountId);
  const client = useApolloClient();
  const [captureFn, { loading: capturing }] = useMutation<CaptureResult>(
    STAFF_PROXY_CAPTURE_DEVICE_SCREENSHOT,
  );

  /** After any action: live detail + (whichever are mounted) activity, staff actions, history. */
  const refetchAfterAction = useCallback(() => {
    void refetchDetail();
    void client.refetchQueries({
      include: 'active',
      onQueryUpdated: (oq) => (oq.queryName ? ACTION_REFETCH_QUERIES.has(oq.queryName) : false),
    });
  }, [client, refetchDetail]);

  const openChildDialog = useCallback(
    (id: string, context?: Record<string, unknown>) => setChildDialog({ id, context }),
    [],
  );
  const closeChildDialog = useCallback(() => setChildDialog(null), []);

  const uuid = detail?.UUID ?? device.UUID;
  const uuids = useMemo(() => [uuid], [uuid]);
  const { statusByUuid } = useDeviceHeartbeats(uuids, DRAWER_HEARTBEAT_POLL_MS);
  const liveStatus = (uuid ? statusByUuid.get(uuid) : undefined) ?? pageLiveStatus;

  const merged = useMemo(
    () => mergeDevice(assign, device, detail),
    [assign, device, detail],
  );
  const status: PillStatus = liveStatus ?? classifyDeviceStatus(merged);

  const classification = detail?.classification ?? null;
  const cap = classification?.managementCapability ?? null;
  const appBeat =
    detail?.liveLastHeartBeat ?? detail?.lastHeartBeat ?? device.lastHeartBeat ?? null;
  const connectivity = classifyConnectivity({
    appBeatTs: appBeat,
    utilsOnline: detail?.utilsOnline,
    utilsLastUpdateDate: detail?.utilsLastUpdateDate,
    liveAppOnline: liveStatus ? liveStatus !== 'offline' : undefined,
  });

  const live = useTestingMdmLiveInfo({
    deviceId: detail ? deviceId : null,
    managementCapability: cap,
    utilsOnline: detail?.utilsOnline,
    appOnline: connectivity.appOnline,
    accountId: scopeAccountId,
  });

  const nowPlaying =
    assign.currentAssignName ?? assign.currentType ?? detail?.currentType ?? null;
  const hdmiActive = detail?.hdmiActive ?? (liveStatus === 'hdmiOff' ? false : undefined);
  const verdict = getScreenHealthVerdict({
    appOnline: detail || liveStatus ? connectivity.appOnline : undefined,
    managementCapability: cap,
    hdmiActive,
    contentExpired: detail?.isContentExpired,
  });

  const accountId = detail?.accountId ?? device.accountId ?? null;
  const canScreenshot = !!detail?.availableCommands?.includes('MDM_CAPTURE_SCREENSHOT');
  const captureBlocked = screenshotBlockedReason({
    utilsVersion: detail?.utilsVersion,
    liveStatus: status,
  });

  /** Take screenshot / Capture now: staff-api captures, waits for the frame, returns its key. */
  const captureScreenshot = useCallback(async () => {
    setTab('content');
    setCaptureNote(null);
    if (!accountId) {
      setCaptureNote('This device has no account id, so a capture cannot be requested.');
      return;
    }
    try {
      const { data } = await captureFn({
        variables: { deviceId: deviceId, accountId },
      });
      const r = data?.staffProxyCaptureDeviceScreenshot;
      if (r?.status === 'captured') {
        toast.success('Screenshot captured');
      } else {
        setCaptureNote(
          r?.reason
            ? `No new screenshot: ${r.reason}`
            : `No new screenshot (${r?.status ?? 'no reply'})`,
        );
      }
    } catch (err) {
      setCaptureNote(`Capture failed: ${(err as Error).message}`);
    } finally {
      refetchAfterAction();
    }
  }, [accountId, deviceId, captureFn, refetchAfterAction]);

  const commands = useTestingDeviceCommands(deviceId, detail?.teamId, scopeAccountId);
  // Live troubleshooting (both modes): the start card's gate reads SecureLink from the live detail —
  // undefined until it loads = disabled. startDeviceTroubleshootSession is generic (no accountId).
  const liveStart = useMemo(
    () => ({
      deviceId: deviceId,
      securelinkStatus: detail?.securelinkStatus ?? null,
      securelinkIp: detail?.securelinkIp ?? null,
    }),
    [deviceId, detail?.securelinkStatus, detail?.securelinkIp],
  );
  // Settings: same gate in both modes (managed hardware only); writes carry the scope accountId.
  const showSettings = !!detail && supportsSettings(cap);
  const CommandDialog = childDialog ? COMMAND_DIALOGS[childDialog.id] : undefined;
  const deviceName = merged.deviceName ?? deviceId;

  const deviceForActions = useMemo(
    () => ({ ...merged, feature: detail?.feature ?? null }),
    [merged, detail?.feature],
  );

  return (
    <DrawerAccountContext.Provider value={scopeAccountId}>
      <div className="flex h-full min-h-0 flex-col">
        <TestingBanner
          name={merged.deviceName ?? deviceId}
          status={status}
          detail={detail}
          device={merged}
          chrome={chrome}
        />

        {scopeAccountId ? (
          <div
            className="shrink-0 border-b border-warning/40 bg-warning/10 px-6 py-1.5 text-xs text-warning"
            data-testid="customer-device-line"
          >
            <span className="font-semibold">{'Customer device'}</span>
            {` · ${companyName?.trim() || scopeAccountId} — actions run on the customer's live screen`}
          </div>
        ) : null}

        {detail ? (
          <TestingActionBar
            device={deviceForActions}
            availableCommands={detail.availableCommands}
            teamId={detail.teamId}
            onSettled={refetchAfterAction}
            openChildDialog={openChildDialog}
            onCaptureScreenshot={captureScreenshot}
            capturing={capturing}
            screenshotBlockedReason={captureBlocked}
          />
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col px-6 pt-4">
          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v as TabId)}
            className="flex min-h-0 flex-1 flex-col gap-4"
          >
            <TabsList className="h-auto w-full shrink-0 justify-start gap-0.5 rounded-none border-b bg-transparent p-0">
              <TabsTrigger
                value="overview"
                className={DRAWER_TAB_TRIGGER}
                data-testid="testing-tab-overview"
              >
                {'Overview'}
              </TabsTrigger>
              {showSettings ? (
                <TabsTrigger
                  value="settings"
                  className={DRAWER_TAB_TRIGGER}
                  data-testid="testing-tab-settings"
                >
                  {'Settings'}
                </TabsTrigger>
              ) : null}
              <TabsTrigger
                value="content"
                className={DRAWER_TAB_TRIGGER}
                data-testid="testing-tab-content"
              >
                {'Content'}
              </TabsTrigger>
              <TabsTrigger
                value="activity"
                className={DRAWER_TAB_TRIGGER}
                data-testid="testing-tab-activity"
              >
                {'Activity'}
              </TabsTrigger>
              <TabsTrigger
                value="troubleshoot"
                className={DRAWER_TAB_TRIGGER}
                data-testid="testing-tab-troubleshoot"
              >
                {'Troubleshooting'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className={cn(TAB_BODY, 'space-y-4')}>
              {detailError ? (
                <div
                  className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                  data-testid="testing-detail-error"
                >
                  {"Couldn't load live device detail: "}
                  {detailError.message}
                </div>
              ) : null}
              {detailLoading && !detail ? (
                <div className="text-sm text-muted-foreground">{'Loading device…'}</div>
              ) : null}

              {detail ? (
                <ConnectivitySection
                  connectivity={connectivity}
                  appBeat={appBeat}
                  managementCapability={cap}
                />
              ) : null}

              <OnTheGlassFacts
                hdmiActive={hdmiActive}
                nowPlaying={nowPlaying}
                selectionDate={detail?.currentSelectionDate}
                contentExpired={detail?.isContentExpired}
                onViewScreenshot={() => setTab('content')}
              />

              {detail ? (
                <DeviceStateCards
                  info={live.info}
                  status={live.status}
                  managementCapability={cap}
                  snapshot={{
                    usedStorage: detail.usedStorage ?? device.usedStorage,
                    totalStorage: detail.totalStorage ?? device.totalStorage,
                    activationDate: detail.activationDate ?? device.activationDate,
                    tz: detail.tz ?? device.tz,
                  }}
                />
              ) : null}

              <div
                className="rounded-lg border bg-card p-3 shadow-sm"
                data-testid="testing-device-info"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">{'Device info'}</span>
                  <Button
                    variant="outline"
                    size="icon-md"
                    aria-label={'Show raw JSON'}
                    title={'Show raw JSON'}
                    onClick={() => setJsonOpen(true)}
                    data-testid="testing-device-json-button"
                  >
                    <Braces className="h-4 w-4" />
                  </Button>
                </div>
                <DeviceInfoList device={merged} onAssignClick={onAssignClick} />
              </div>

              <details
                className="group rounded-lg border bg-card shadow-sm"
                data-testid="testing-capabilities"
              >
                <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-muted-foreground">
                  {'Capabilities'}
                  {merged.availableCommands?.length ? (
                    <span className="ml-2 font-normal">
                      {`${merged.availableCommands.length} available`}
                    </span>
                  ) : null}
                </summary>
                <div className="border-t p-2">
                  <DeviceCapabilityDiagnostic device={merged} />
                </div>
              </details>
            </TabsContent>

            {showSettings && detail ? (
              <TabsContent value="settings" className={TAB_BODY}>
                <SettingsTab
                  key={deviceId}
                  detail={detail}
                  liveSettings={live.info?.settings}
                  commands={commands}
                  onSaved={refetchAfterAction}
                />
              </TabsContent>
            ) : null}

            <TabsContent value="content" className={TAB_BODY}>
              <ContentTab
                deviceId={deviceId}
                accountId={detail?.accountId ?? device.accountId}
                takeScreens={detail?.takeScreens}
                verdict={verdict}
                hdmiActive={hdmiActive}
                appOnline={detail || liveStatus ? connectivity.appOnline : undefined}
                currentType={assign.currentType ?? detail?.currentType ?? null}
                nowPlayingName={assign.currentAssignName ?? null}
                selectionDate={detail?.currentSelectionDate}
                documentDuration={detail?.documentDuration}
                contentExpired={detail?.isContentExpired}
                onCaptureNow={canScreenshot ? () => void captureScreenshot() : undefined}
                capturing={capturing}
                captureBlockedReason={captureBlocked}
                captureNote={captureNote}
              />
            </TabsContent>

            <TabsContent value="activity" className={TAB_BODY}>
              <ActivityTab deviceId={deviceId} accountId={scopeAccountId} />
            </TabsContent>

            <TabsContent value="troubleshoot" className={cn(TAB_BODY, 'flex flex-col')}>
              {uuid ? (
                <DeviceTroubleshootTab deviceUuid={uuid} live={liveStart} />
              ) : (
                <div className="text-sm text-muted-foreground">
                  {'This device has no UUID yet (unpaired) — no troubleshooting sessions.'}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <DeviceJsonDialog open={jsonOpen} device={merged} onClose={() => setJsonOpen(false)} />

        {/* The action-bar dialogs (both modes) — they read the scope accountId from DrawerAccountContext. */}
        <TestingRemoteControlDialog
          open={childDialog?.id === 'remoteControl'}
          deviceName={merged.deviceName ?? deviceId}
          remoteUrl={
            typeof childDialog?.context?.remoteUrl === 'string' ? childDialog.context.remoteUrl : null
          }
          onClose={closeChildDialog}
        />
        <TestingDeviceInfoDialog
          open={childDialog?.id === 'mdmDeviceInfo'}
          deviceId={deviceId}
          onClose={closeChildDialog}
        />
        <TestingCommandHistoryDialog
          open={childDialog?.id === 'commandHistory'}
          deviceId={deviceId}
          onClose={closeChildDialog}
        />
        {CommandDialog && detail ? (
          <CommandDialog
            open
            detail={detail}
            deviceName={deviceName}
            commands={commands}
            onClose={closeChildDialog}
            onDone={refetchAfterAction}
          />
        ) : null}
        {childDialog?.id === 'storageDetails' ? (
          <TestingStorageDialog
            open
            deviceId={deviceId}
            reply={childDialog.context?.reply}
            onClose={closeChildDialog}
          />
        ) : null}
      </div>
    </DrawerAccountContext.Provider>
  );
}

function StatusPill({ status }: { status: PillStatus }) {
  const pill = PILL[status];
  return (
    <span
      className="ml-1 inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-background px-2 py-0.5 text-xs font-medium"
      data-testid="testing-drawer-status"
    >
      <span className={cn('inline-block h-2 w-2 rounded-full', pill.dot)} aria-hidden />
      <span className={pill.text}>{pill.label}</span>
    </span>
  );
}

function BannerDot() {
  return (
    <span className="text-white/40" aria-hidden>
      ·
    </span>
  );
}

/** Full classification for the shared formatters, or null when open-api sent none. */
function fullClassification(
  c: TestingDeviceDetail['classification'] | null | undefined,
): DeviceClassification | null {
  if (!c) return null;
  return {
    platform: 'unknown',
    vendor: 'unknown',
    hardwareFamily: 'unknown',
    managementCapability: 'none',
    osVersionDisplay: '',
    osVersionMajor: null,
    modelCode: null,
    manufacturerRaw: null,
    platformRaw: null,
    ...c,
  } as DeviceClassification;
}

/** "—" is the formatters' "unknown" — treat it as absent. */
function known(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t && t !== '—' ? t : null;
}

/**
 * Branded platform banner — port of scio3's drawer header: OptiSigns teal gradient, white text,
 * device tile + name + status pill, ⤢ / ✕ top-right; row 2 friendly model · OS · management tier with
 * the OptiSigns mark + app version on the right; row 3 fleet · serial (quiet).
 */
function TestingBanner({
  name,
  status,
  detail,
  device,
  chrome,
}: {
  name: string;
  status: PillStatus;
  detail: TestingDeviceDetail | null;
  device: MdmDevice;
  chrome: BannerChrome;
}) {
  const classification = fullClassification(detail?.classification);
  const { label: capLabel, Icon: CapIcon } = getCapabilityDisplay(
    classification?.managementCapability,
  );
  // scio3 formatting: friendly model ("OptiStick Gen 3") and per-runtime OS ("Android 11"); raw values
  // only when the formatters have nothing better.
  let model: string | null = null;
  let os: string | null = null;
  if (classification) {
    try {
      model = known(formatModel(classification));
      os = known(formatOs(classification));
    } catch {
      /* partial classification → raw fallbacks below */
    }
  }
  model ??= known(classification?.modelCode) ?? known(detail?.model ?? device.model);
  os ??= known(
    [detail?.os ?? device.os, classification?.osVersionDisplay].filter(Boolean).join(' '),
  );
  const segs = [model, os].filter((s): s is string => !!s);

  const fleetId = detail?.fleetId ?? device.fleetId ?? null;
  const serial = detail?.serialNo ?? device.serialNo ?? null;
  const appVersion = detail?.localAppVersion ?? device.localAppVersion ?? null;
  // Fleet names aren't readable from staff-api (no fleets op) — a non-default fleet shows its id.
  const line3 = [fleetId ? `Fleet ${fleetId}` : 'Default'];
  if (serial) line3.push(`SN ${serial}`);

  return (
    <InShellPanelHeader className="relative shrink-0 space-y-0 bg-gradient-to-br from-primary-dark to-primary px-6 pb-4 pt-5 text-left text-white">
      <div className="absolute right-3 top-3 flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-xs"
          className="cursor-pointer text-white hover:bg-white/15 hover:text-white"
          onClick={chrome.onToggleExpand}
          aria-label={chrome.expanded ? 'Collapse' : 'Expand'}
          title={chrome.expanded ? 'Collapse' : 'Expand'}
          data-testid="testing-drawer-expand"
        >
          {chrome.expanded ? (
            <Minimize2 className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Maximize2 className="h-3.5 w-3.5" aria-hidden />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          className="cursor-pointer text-white hover:bg-white/15 hover:text-white"
          onClick={chrome.onClose}
          aria-label={'Close'}
          title={'Close'}
          data-testid="testing-drawer-close"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>

      <InShellPanelTitle
        className="flex items-center gap-2 pr-16 text-white"
        data-testid="testing-drawer-title"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
          <Monitor className="h-4 w-4" aria-hidden />
        </span>
        <span className="truncate text-lg font-semibold">{name}</span>
        <StatusPill status={status} />
      </InShellPanelTitle>

      <div className="mt-2.5 flex items-end justify-between gap-3">
        {detail ? (
          <div
            className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-white/90"
            data-testid="testing-drawer-platform"
          >
            {segs.map((s, i) => (
              <span key={s} className="inline-flex items-center gap-x-1.5">
                {i > 0 ? <BannerDot /> : null}
                <span className={i === 0 ? 'font-medium' : undefined}>{s}</span>
              </span>
            ))}
            {segs.length > 0 ? <BannerDot /> : null}
            <span
              className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-xs font-medium"
              data-testid="testing-capability-badge"
            >
              <CapIcon className="h-3 w-3" aria-hidden />
              {capLabel}
            </span>
          </div>
        ) : (
          <span />
        )}
        <div className="shrink-0 text-right leading-tight" data-testid="testing-drawer-brand">
          <div className="flex items-center justify-end gap-1 text-[13px] font-semibold tracking-wide">
            <Cloud className="h-3.5 w-3.5" aria-hidden />
            {'OptiSigns'}
          </div>
          {appVersion ? (
            <div className="mt-0.5 text-[11px] text-white/70">{`v${appVersion}`}</div>
          ) : null}
        </div>
      </div>

      <div
        className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-white/60"
        data-testid="testing-drawer-subtitle"
      >
        {line3.map((s, i) => (
          <span key={s} className="inline-flex items-center gap-1.5">
            {i > 0 ? (
              <span className="text-white/30" aria-hidden>
                ·
              </span>
            ) : null}
            <span className="truncate">{s}</span>
          </span>
        ))}
      </div>

      <InShellPanelDescription className="sr-only">{'Device detail panel'}</InShellPanelDescription>
    </InShellPanelHeader>
  );
}
